import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AboutDialog } from "./components/AboutDialog";
import { FeedbackDialog } from "./components/FeedbackDialog";
import { Gallery } from "./components/Gallery";
import { ShareDialog } from "./components/ShareDialog";
import { StateMessage } from "./components/StateMessage";
import { StatusBar } from "./components/StatusBar";
import { Toolbar } from "./components/Toolbar";
import { WorkspaceHeader } from "./components/WorkspaceHeader";
import { useApps } from "./hooks/useApps";
import type { AppEntry, Locale } from "./types";

function filterApps(
  apps: AppEntry[],
  query: string,
  category: string,
  lng: Locale,
): AppEntry[] {
  const q = query.trim().toLowerCase();
  return apps.filter(app => {
    const catId = app.category.en || app.category.es;
    if (category !== "all" && catId !== category) return false;
    if (!q) return true;

    const haystack = [
      app.name,
      app.description[lng],
      app.description.es,
      app.category[lng],
      ...app.tags,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export default function App() {
  const { t, i18n } = useTranslation("launcher");
  const lng: Locale = i18n.resolvedLanguage === "en" ? "en" : "es";

  const { apps, isLoading, error } = useApps();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [aboutOpen, setAboutOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const filtered = useMemo(
    () => filterApps(apps, query, category, lng),
    [apps, query, category, lng],
  );

  return (
    <div className="h-full flex flex-col bg-surface-base text-text-main">
      <WorkspaceHeader
        onShare={() => setShareOpen(true)}
        onAbout={() => setAboutOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
      />

      <main role="main" className="flex-1 min-h-0 p-2 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-3 py-4">
          <section className="mb-4 flex flex-col gap-1">
            <h1 className="m-0 label-sm text-text-muted">{t("title")}</h1>
            <p className="m-0 text-13 text-text-muted">{t("subtitle")}</p>
          </section>

          <Toolbar
            apps={apps}
            query={query}
            onQueryChange={setQuery}
            category={category}
            onCategoryChange={setCategory}
          />

          {isLoading && (
            <StateMessage
              kind="loading"
              icon="hourglass_empty"
              title={t("state.loading.title")}
              hint={t("state.loading.hint")}
            />
          )}

          {!isLoading && error && (
            <StateMessage
              kind="error"
              icon="error"
              title={t("state.error.title")}
              hint={t("state.error.hint")}
            />
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <StateMessage
              kind="empty"
              icon="search_off"
              title={t("state.empty.title")}
              hint={
                query
                  ? t("state.empty.hintQuery", { query })
                  : t("state.empty.hintNoQuery")
              }
            />
          )}

          {!isLoading && !error && filtered.length > 0 && <Gallery apps={filtered} />}
        </div>
      </main>

      <StatusBar filtered={filtered.length} total={apps.length} />

      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
